/**
 * Main App - Business Assistant
 *
 * Uses AppShell layout with five main views:
 * - Home: AI chat interface
 * - Oggi: Daily timeline view
 * - Progetti: Projects management
 * - Hub: Contacts and conversations
 * - Contabilita: Accounting and invoices
 *
 * Each view is self-contained and manages its own state via Zustand stores.
 */

import { useCallback } from 'react';
import { AppShell, type ViewType, type DrawerSection } from './layouts/AppShell';
import { HomeView, TodayView, ProjectsView, CalendarView, HubView, AccountingView, SettingsView } from './views';

/**
 * Drawer content configuration for each view
 * NOTE: Keys must match ViewType (English names for routes)
 */
const DRAWER_CONTENT: Record<ViewType, DrawerSection[]> = {
  home: [
    {
      title: 'Assistant',
      items: [
        { id: 'all', label: 'All chats', hint: 'Conversation history' },
        { id: 'today', label: 'Today', hint: 'Today\'s chats' },
        { id: 'projects', label: 'Projects', hint: 'Chats by project' },
      ],
    },
  ],
  today: [
    {
      title: 'Filters',
      items: [
        { id: 'all', label: 'All', hint: '' },
        { id: 'email', label: 'Email', hint: '' },
        { id: 'call', label: 'Calls', hint: '' },
        { id: 'message', label: 'Messages', hint: '' },
        { id: 'task', label: 'Task', hint: '' },
        { id: 'event', label: 'Events', hint: '' },
        { id: 'reminder', label: 'Reminder', hint: '' },
        { id: 'invoiceIn', label: 'Invoices', hint: '' },
      ],
    },
  ],
  projects: [
    {
      title: 'Status',
      items: [
        { id: 'all', label: 'All', hint: '' },
        { id: 'in-corso', label: 'In progress', hint: '' },
        { id: 'da-iniziare', label: 'Not started', hint: '' },
        { id: 'a-rischio', label: 'At risk', hint: '' },
        { id: 'chiusi', label: 'Closed', hint: '' },
      ],
    },
  ],
  calendar: [
    {
      title: 'Views',
      items: [
        { id: 'month', label: 'Month', hint: '' },
        { id: 'week', label: 'Week', hint: '' },
        { id: 'day', label: 'Day', hint: '' },
        { id: 'agenda', label: 'Agenda', hint: '' },
      ],
    },
  ],
  hub: [
    {
      title: 'Filters',
      items: [
        { id: 'all', label: 'All', hint: '' },
        { id: 'favorite', label: 'Favorites', hint: '' },
        { id: 'recent', label: 'Recent', hint: '' },
        { id: 'lead', label: 'Lead', hint: '' },
        { id: 'team', label: 'Team', hint: '' },
      ],
    },
  ],
  accounting: [
    {
      title: 'Sections',
      items: [
        { id: 'all', label: 'All', hint: '' },
        { id: 'entrate', label: 'Income', hint: '' },
        { id: 'uscite', label: 'Expenses', hint: '' },
        { id: 'non-incassate', label: 'Uncollected', hint: '' },
        { id: 'da-pagare', label: 'To pay', hint: '' },
        { id: 'forecast', label: 'Forecast', hint: '' },
      ],
    },
  ],
  settings: [
    {
      title: 'Sections',
      items: [
        { id: 'profile', label: 'Profile', hint: 'Personal data' },
        { id: 'account', label: 'Account', hint: 'Security and access' },
        { id: 'preferences', label: 'Preferences', hint: 'Theme and language' },
        { id: 'integrations', label: 'Integrations', hint: 'Connected services' },
        { id: 'company', label: 'Company', hint: 'Company data' },
      ],
    },
  ],
};

/**
 * Main App component
 */
export default function App() {
  // Note: Mock data seeding is handled by useAppInit in routes.tsx
  // Handlers
  const handlePromptSubmit = useCallback((text: string, webSearch: boolean) => {
    console.log('Prompt submitted:', { text, webSearch });
  }, []);

  const handleSearchClick = useCallback(() => {
    console.log('Search clicked');
  }, []);

  const handleNotificationsClick = useCallback(() => {
    console.log('Notifications clicked');
  }, []);

  // Views content - each view is self-contained
  // NOTE: Keys must match ViewType (English names for routes)
  const views = {
    home: <HomeView />,
    today: <TodayView />,
    projects: <ProjectsView />,
    calendar: <CalendarView />,
    hub: <HubView />,
    accounting: <AccountingView />,
    settings: <SettingsView />,
  };

  return (
    <AppShell
      views={views}
      drawerContent={DRAWER_CONTENT}
      onPromptSubmit={handlePromptSubmit}
      onSearchClick={handleSearchClick}
      onNotificationsClick={handleNotificationsClick}
    />
  );
}
